import { AlertTriangle } from 'lucide-react';
import type { PriorityLimitDetails, PriorityLimitTask } from '@pms/shared';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { formatDate } from '@/lib/dateFormat';
import { TaskStatusBadge, TaskTypeBadge } from './TaskBadges';
import { initials } from './taskConstants';

interface PriorityLimitDialogProps {
  details: PriorityLimitDetails | null;
  onClose: () => void;
}

/** "08 Sep 2026 → 22 Sep 2026", or nothing for a task without a date range. */
function period(task: PriorityLimitTask): string | null {
  if (!task.periodic_start_date && !task.periodic_end_date) return null;
  const from = task.periodic_start_date ? formatDate(task.periodic_start_date) : '—';
  const to = task.periodic_end_date ? formatDate(task.periodic_end_date) : '—';
  return `${from} → ${to}`;
}

/**
 * Explains a priority-cap rejection by naming the tasks that caused it.
 *
 * The server sends the blocking tasks as structured data precisely so this can list them —
 * the prose message only says how many there are, which is not enough to act on.
 *
 * Opens over whichever modal triggered it (Create Task, Edit Task, Manage Assignees). That
 * is a deliberate exception to the project's no-nested-modals rule: the alternative is
 * closing the form the person was filling in, discarding their work. Its `open` state is
 * its own, so dismissing it never reaches the parent.
 */
export function PriorityLimitDialog({ details, onClose }: PriorityLimitDialogProps) {
  if (!details) return null;

  return (
    <Dialog
      open
      onOpenChange={(v) => { if (!v) onClose(); }}
    >
      <DialogContent
        // Anchored top-right instead of centred. DialogContent centres itself with
        // left-1/2/top-1/2 plus negative translates, so all four have to be overridden —
        // cn() runs twMerge, which resolves each conflicting utility to the last one.
        // Only this dialog moves; the shared component is untouched for its ~30 other uses.
        className="left-auto top-4 right-4 max-w-md translate-x-0 translate-y-0 gap-2 p-4 sm:p-4"
        // Without these the Escape key and the outside click also reach the modal
        // underneath, closing the form and losing everything typed into it.
        onEscapeKeyDown={(e) => e.stopPropagation()}
        onPointerDownOutside={(e) => e.stopPropagation()}
        onInteractOutside={(e) => e.stopPropagation()}
      >
        {/* pr-6 keeps the title clear of the built-in close button in the corner. */}
        <DialogHeader className="space-y-1 pr-6">
          <DialogTitle className="flex items-center gap-2 text-base">
            <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-600 dark:bg-rose-500/15 dark:text-rose-400">
              <AlertTriangle className="h-3.5 w-3.5" />
            </span>
            Priority task limit reached
          </DialogTitle>
          <DialogDescription className="text-xs leading-snug">
            One person can hold at most {details.max} priority tasks at a time. Finish or
            stop one of the tasks below before assigning another.
          </DialogDescription>
        </DialogHeader>

        <div className="max-h-[70vh] space-y-2 overflow-y-auto">
          {details.users.map((user) => (
            <div key={user.user_id} className="rounded-lg border border-rose-200 bg-rose-50/50 p-2.5 dark:border-rose-500/30 dark:bg-rose-500/5">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-rose-100 text-[10px] font-semibold text-rose-700 dark:bg-rose-500/20 dark:text-rose-300">
                  {initials(user.name.split(' ')[0], user.name.split(' ')[1])}
                </span>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold leading-tight text-foreground">{user.name}</p>
                  <p className="text-[11px] leading-tight text-muted-foreground">
                    {user.priority_count} of {details.max} priority tasks in progress
                  </p>
                </div>
              </div>

              <ul className="mt-2 space-y-1.5">
                {user.tasks.map((task) => {
                  const range = period(task);
                  return (
                    <li key={task.id} className="rounded-md border border-border/60 bg-card px-2.5 py-1.5">
                      <p className="flex gap-1.5 text-sm leading-tight">
                        <span className="shrink-0 text-muted-foreground">Title:</span>
                        <span className="truncate font-medium text-foreground">{task.title}</span>
                      </p>
                      {task.description && (
                        <p className="mt-0.5 flex gap-1.5 text-[11px] leading-tight text-muted-foreground">
                          <span className="shrink-0">Description:</span>
                          {/* Clamped: descriptions run to 2000 chars, and two of them
                              would push the badges and dates out of view. */}
                          <span className="line-clamp-1 whitespace-pre-wrap break-words">
                            {task.description}
                          </span>
                        </p>
                      )}
                      <div className="mt-1 flex flex-wrap items-center gap-1">
                        <TaskTypeBadge type={task.task_type} />
                        <TaskStatusBadge status={task.status} />
                        {range && <span className="text-[11px] text-muted-foreground">{range}</span>}
                      </div>
                      {task.created_by_name && (
                        <p className="mt-0.5 text-[11px] leading-tight text-muted-foreground">
                          Assigned by {task.created_by_name}
                        </p>
                      )}
                    </li>
                  );
                })}
                {user.tasks.length === 0 && (
                  <li className="text-xs text-muted-foreground">
                    Could not load the blocking tasks.
                  </li>
                )}
              </ul>
            </div>
          ))}
        </div>

        <DialogFooter>
          <Button type="button" size="sm" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
